import { useCallback, useLayoutEffect, useRef, useState } from "react";
import {
  Panel,
  Group as PanelGroup,
  Separator as PanelResizeHandle,
  type PanelSize,
  useGroupRef,
  usePanelRef,
} from "react-resizable-panels";
import { toast } from "sonner";

import { getEncounterReportPdf } from "@elation/api-client/src/stitch-web";

import { VisitTour } from "@src/features/tourguide";
import { VerticalFlexbox } from "@src/shared/components/layout/Flexbox";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/shared/components/ui/tabs";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";

import { EncounterReportEntity } from "../../../../application/encounter-report.entity";
import type { PatientSelectorPatientSummaryV2 } from "../PatientSelector";
import { VisitNoteEditor } from "../VisitNoteEditor";
import { CompletedVisitAgendaPanel } from "./components/CompletedVisitAgendaPanel";
import {
  CompletedVisitCodesCollapsiblePanel,
  CompletedVisitCodesPanel,
} from "./components/CompletedVisitCodesPanel";
import { CompletedVisitHeader } from "./components/CompletedVisitHeader";
import { CompletedVisitTranscriptPanel } from "./components/CompletedVisitTranscriptPanel";

const COLLAPSED_PANEL_SIZE = 38;
const EXPANDED_PANEL_SIZE = 180;
const MAX_PANEL_SIZE = 420;

export interface CompletedVisitProps {
  patient?: PatientSelectorPatientSummaryV2 | null;
  noteText?: string | null;
  encounterId?: number;
}

export function CompletedVisit({
  patient,
  noteText,
  encounterId,
}: CompletedVisitProps) {
  const [agendaOpen, setAgendaOpen] = useState(false);
  const [codesOpen, setCodesOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const agendaPanelRef = usePanelRef();
  const codesPanelRef = usePanelRef();
  const panelGroupRef = useGroupRef();
  const panelGroupElementRef = useRef<HTMLDivElement | null>(null);
  const agendaOpenRef = useRef(false);
  const codesOpenRef = useRef(false);
  const isGeneratingReportRef = useRef(false);
  const activeResizeTargetRef = useRef<"agenda" | "codes" | null>(null);
  const isApplyingLayoutRef = useRef(false);
  const agendaExpandedSizeRef = useRef(EXPANDED_PANEL_SIZE);
  const codesExpandedSizeRef = useRef(EXPANDED_PANEL_SIZE);
  const resizeDockedPanels = useCallback(
    (nextAgendaOpen = agendaOpen, nextCodesOpen = codesOpen) => {
      const groupHeight =
        panelGroupElementRef.current?.getBoundingClientRect().height ?? 760;
      const separatorHeight =
        (nextAgendaOpen ? 12 : 8) + (nextCodesOpen ? 12 : 8);
      const availableHeight = Math.max(1, groupHeight - separatorHeight);
      const agendaHeight = nextAgendaOpen
        ? agendaExpandedSizeRef.current
        : COLLAPSED_PANEL_SIZE;
      const codesHeight = nextCodesOpen
        ? codesExpandedSizeRef.current
        : COLLAPSED_PANEL_SIZE;
      const noteHeight = Math.max(
        220,
        availableHeight - agendaHeight - codesHeight,
      );
      const totalHeight = noteHeight + agendaHeight + codesHeight;

      panelGroupRef.current?.setLayout({
        "note-editor": (noteHeight / totalHeight) * 100,
        "agenda-panel": (agendaHeight / totalHeight) * 100,
        "codes-panel": (codesHeight / totalHeight) * 100,
      });
    },
    [agendaOpen, codesOpen, panelGroupRef],
  );
  const setDockedPanelLayout = useCallback(
    (agendaHeight: number, codesHeight: number) => {
      const groupHeight =
        panelGroupElementRef.current?.getBoundingClientRect().height ?? 760;
      const separatorHeight =
        (agendaOpenRef.current ? 12 : 8) + (codesOpenRef.current ? 12 : 8);
      const availableHeight = Math.max(1, groupHeight - separatorHeight);
      const noteHeight = Math.max(
        220,
        availableHeight - agendaHeight - codesHeight,
      );
      const totalHeight = noteHeight + agendaHeight + codesHeight;

      isApplyingLayoutRef.current = true;
      panelGroupRef.current?.setLayout({
        "note-editor": (noteHeight / totalHeight) * 100,
        "agenda-panel": (agendaHeight / totalHeight) * 100,
        "codes-panel": (codesHeight / totalHeight) * 100,
      });
      window.requestAnimationFrame(() => {
        isApplyingLayoutRef.current = false;
      });
    },
    [panelGroupRef],
  );
  const handleDockedPanelLayoutChange = useCallback(
    (layout: Record<string, number>) => {
      const activeTarget = activeResizeTargetRef.current;
      if (!activeTarget || isApplyingLayoutRef.current) return;

      const groupHeight =
        panelGroupElementRef.current?.getBoundingClientRect().height ?? 760;
      const separatorHeight =
        (agendaOpenRef.current ? 12 : 8) + (codesOpenRef.current ? 12 : 8);
      const availableHeight = Math.max(1, groupHeight - separatorHeight);
      const layoutToPixels = (panelId: string) =>
        ((layout[panelId] ?? 0) / 100) * availableHeight;

      if (activeTarget === "agenda" && agendaOpenRef.current) {
        const agendaHeight = Math.min(
          Math.max(layoutToPixels("agenda-panel"), EXPANDED_PANEL_SIZE),
          MAX_PANEL_SIZE,
        );
        agendaExpandedSizeRef.current = agendaHeight;
        setDockedPanelLayout(
          agendaHeight,
          codesOpenRef.current
            ? codesExpandedSizeRef.current
            : COLLAPSED_PANEL_SIZE,
        );
      }

      if (activeTarget === "codes" && codesOpenRef.current) {
        const codesHeight = Math.min(
          Math.max(layoutToPixels("codes-panel"), EXPANDED_PANEL_SIZE),
          MAX_PANEL_SIZE,
        );
        codesExpandedSizeRef.current = codesHeight;
        setDockedPanelLayout(
          agendaOpenRef.current
            ? agendaExpandedSizeRef.current
            : COLLAPSED_PANEL_SIZE,
          codesHeight,
        );
      }
    },
    [setDockedPanelLayout],
  );
  const startDockedPanelResize = useCallback((target: "agenda" | "codes") => {
    activeResizeTargetRef.current = target;
    const stopResize = () => {
      activeResizeTargetRef.current = null;
      window.removeEventListener("pointerup", stopResize);
      window.removeEventListener("pointercancel", stopResize);
    };
    window.addEventListener("pointerup", stopResize);
    window.addEventListener("pointercancel", stopResize);
  }, []);
  useLayoutEffect(() => {
    agendaOpenRef.current = agendaOpen;
    codesOpenRef.current = codesOpen;
    resizeDockedPanels();
  }, [agendaOpen, codesOpen, resizeDockedPanels]);
  const scheduleDockedPanelResize = useCallback(
    (nextAgendaOpen: boolean, nextCodesOpen: boolean) => {
      requestAnimationFrame(() => {
        resizeDockedPanels(nextAgendaOpen, nextCodesOpen);
      });
      window.setTimeout(() => {
        resizeDockedPanels(nextAgendaOpen, nextCodesOpen);
      }, 50);
      window.setTimeout(() => {
        resizeDockedPanels(nextAgendaOpen, nextCodesOpen);
      }, 250);
    },
    [resizeDockedPanels],
  );
  const setAgendaPanelOpen = useCallback(
    (nextOpen: boolean) => {
      agendaOpenRef.current = nextOpen;
      setAgendaOpen(nextOpen);
      scheduleDockedPanelResize(nextOpen, codesOpenRef.current);
    },
    [scheduleDockedPanelResize],
  );
  const setCodesPanelOpen = useCallback(
    (nextOpen: boolean) => {
      codesOpenRef.current = nextOpen;
      setCodesOpen(nextOpen);
      scheduleDockedPanelResize(agendaOpenRef.current, nextOpen);
    },
    [scheduleDockedPanelResize],
  );
  const rememberAgendaPanelSize = useCallback(
    ({ inPixels }: PanelSize) => {
      if (!agendaOpen || inPixels <= COLLAPSED_PANEL_SIZE) return;
      agendaExpandedSizeRef.current = Math.min(inPixels, MAX_PANEL_SIZE);
    },
    [agendaOpen],
  );
  const rememberCodesPanelSize = useCallback(
    ({ inPixels }: PanelSize) => {
      if (!codesOpen || inPixels <= COLLAPSED_PANEL_SIZE) return;
      codesExpandedSizeRef.current = Math.min(inPixels, MAX_PANEL_SIZE);
    },
    [codesOpen],
  );
  const downloadEncounterReport = useCallback(async () => {
    if (encounterId == null || isGeneratingReportRef.current) return;

    isGeneratingReportRef.current = true;
    setIsGeneratingReport(true);
    const loadingToastId = toast.loading("Generating encounter report...");

    try {
      const { data: pdf } = await getEncounterReportPdf(encounterId);
      EncounterReportEntity.download(pdf, `encounter-${encounterId}.pdf`);
      toast.success("Encounter report downloaded.", { id: loadingToastId });
    } catch (error) {
      toast.dismiss(loadingToastId);
      toast.error(
        getServerErrorMessage(error) ??
          "Unable to download the encounter report. Please try again.",
      );
    } finally {
      isGeneratingReportRef.current = false;
      setIsGeneratingReport(false);
    }
  }, [encounterId]);

  return (
    <VerticalFlexbox className="mx-auto h-full min-h-0 w-full max-w-[980px] bg-background">
      <CompletedVisitHeader patient={patient} encounterId={encounterId} />

      <Tabs defaultValue="note" className="flex h-full min-h-0 flex-1 flex-col">
        <TabsList className="h-9 justify-start rounded-none border-border border-b bg-background px-[18px] py-0">
          <TabsTrigger
            value="note"
            className="h-7 rounded-md px-2.5 text-[12px] data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
          >
            Note
          </TabsTrigger>
          <TabsTrigger
            value="codes"
            className="h-7 rounded-md px-2.5 text-[12px] data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
          >
            Codes
          </TabsTrigger>
          <TabsTrigger
            value="transcript"
            className="h-7 rounded-md px-2.5 text-[12px] data-[state=active]:bg-primary/10 data-[state=active]:text-primary data-[state=active]:shadow-none"
          >
            Transcript
          </TabsTrigger>
        </TabsList>

        <TabsContent
          value="note"
          className="mt-0 min-h-0 flex-1 overflow-hidden focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 [&_*]:focus-visible:outline-none [&_*]:focus-visible:ring-0 [&_*]:focus-visible:ring-offset-0"
        >
          <div className="h-full min-h-0 px-[18px] py-3">
            <PanelGroup
              orientation="vertical"
              className="h-full min-h-0 [&_[data-panel]]:outline-none [&_[data-separator]]:outline-none"
              elementRef={panelGroupElementRef}
              groupRef={panelGroupRef}
              onLayoutChange={handleDockedPanelLayoutChange}
              resizeTargetMinimumSize={{ coarse: 28, fine: 12 }}
            >
              <Panel id="note-editor" minSize="220px" className="outline-none">
                <VisitTour.NoteStep className="flex h-full min-h-0">
                  <VisitNoteEditor
                    value={noteText}
                    ariaLabel="Completed visit note editor"
                    minHeightClassName="min-h-0"
                    onPrint={downloadEncounterReport}
                    printLabel="Download report"
                    printDisabled={encounterId == null}
                    isPrinting={isGeneratingReport}
                  />
                </VisitTour.NoteStep>
              </Panel>

              <PanelResizeHandle
                id="agenda-resize-handle"
                disabled={!agendaOpen}
                className={
                  agendaOpen
                    ? "group/resize flex h-3 w-full cursor-ns-resize items-center justify-center bg-background outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                    : "h-2 w-full bg-background"
                }
                aria-label="Resize agenda panel"
                onPointerDown={() => startDockedPanelResize("agenda")}
              >
                {agendaOpen ? (
                  <span className="h-1 w-16 rounded-full bg-muted-foreground/30 opacity-0 transition-opacity group-hover/resize:opacity-100" />
                ) : null}
              </PanelResizeHandle>

              <Panel
                id="agenda-panel"
                panelRef={agendaPanelRef}
                minSize={
                  agendaOpen ? EXPANDED_PANEL_SIZE : COLLAPSED_PANEL_SIZE
                }
                defaultSize={COLLAPSED_PANEL_SIZE}
                maxSize="420px"
                groupResizeBehavior="preserve-pixel-size"
                onResize={rememberAgendaPanelSize}
              >
                <CompletedVisitAgendaPanel
                  encounterId={encounterId}
                  open={agendaOpen}
                  onOpenChange={setAgendaPanelOpen}
                />
              </Panel>

              <PanelResizeHandle
                id="codes-resize-handle"
                disabled={!codesOpen}
                className={
                  codesOpen
                    ? "group/resize flex h-3 w-full cursor-ns-resize items-center justify-center bg-background outline-none transition-colors hover:bg-muted/50 focus-visible:bg-muted/50"
                    : "h-2 w-full bg-background"
                }
                aria-label="Resize codes panel"
                onPointerDown={() => startDockedPanelResize("codes")}
              >
                {codesOpen ? (
                  <span className="h-1 w-16 rounded-full bg-muted-foreground/30 opacity-0 transition-opacity group-hover/resize:opacity-100" />
                ) : null}
              </PanelResizeHandle>

              <Panel
                id="codes-panel"
                panelRef={codesPanelRef}
                minSize={codesOpen ? EXPANDED_PANEL_SIZE : COLLAPSED_PANEL_SIZE}
                defaultSize={COLLAPSED_PANEL_SIZE}
                maxSize="420px"
                groupResizeBehavior="preserve-pixel-size"
                onResize={rememberCodesPanelSize}
              >
                <VisitTour.CodesStep className="flex h-full min-h-0">
                  <CompletedVisitCodesCollapsiblePanel
                    encounterId={encounterId}
                    open={codesOpen}
                    onOpenChange={setCodesPanelOpen}
                    compact
                  />
                </VisitTour.CodesStep>
              </Panel>
            </PanelGroup>
          </div>
        </TabsContent>

        <TabsContent
          value="codes"
          className="mt-0 min-h-0 flex-1 px-[18px] py-4"
        >
          <CompletedVisitCodesPanel encounterId={encounterId} />
        </TabsContent>

        <TabsContent value="transcript" className="mt-0 min-h-0 flex-1">
          <CompletedVisitTranscriptPanel encounterId={encounterId} />
        </TabsContent>
      </Tabs>
    </VerticalFlexbox>
  );
}
